import { useState } from 'react';
import { Menu, X } from 'lucide-react';
import { Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { SidebarNav } from '@/components/layout/Sidebar';
import { SubscriptionBanner } from '@/components/layout/SubscriptionBanner';
import { UserMenu } from '@/components/layout/UserMenu';
import { Button } from '@/components/ui/button';
import { Toaster } from '@/components/ui/toaster';
import { cn } from '@/lib/utils';

/**
 * Sidebar (desktop) / drawer (mobile) shell around every authenticated route.
 * One layout serves web, the responsive breakpoint, and the Capacitor Android
 * build alike — there is no separate mobile app to keep in sync.
 */
export function AppShell() {
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <div className="flex min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden w-64 shrink-0 flex-col bg-grad-sidebar lg:flex">
        <SidebarHeader />
        <SidebarNav />
      </aside>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setDrawerOpen(false)}
            aria-hidden
          />
          <aside className="absolute inset-y-0 left-0 flex w-72 flex-col bg-grad-sidebar shadow-2xl animate-in slide-in-from-left">
            <div className="flex items-center justify-between px-4 py-4">
              <BrandMark />
              <Button
                variant="ghost"
                size="icon"
                className="text-white hover:bg-white/10"
                onClick={() => setDrawerOpen(false)}
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            <SidebarNav onNavigate={() => setDrawerOpen(false)} />
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-16 shrink-0 items-center gap-3 border-b border-border/60 bg-background/80 px-4 backdrop-blur">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <div className="flex-1" />
          <UserMenu />
        </header>

        <SubscriptionBanner />

        <main className={cn('flex-1 overflow-y-auto p-4 sm:p-6')}>
          <Outlet />
        </main>
      </div>

      <Toaster />
    </div>
  );
}

function BrandMark() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2.5">
      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-grad-gold font-display text-lg font-bold text-ink-950">
        F
      </div>
      <span className="font-display text-xl tracking-tight text-white">{t('appName')}</span>
    </div>
  );
}

function SidebarHeader() {
  return (
    <div className="flex h-16 shrink-0 items-center border-b border-white/10 px-5">
      <BrandMark />
    </div>
  );
}
